import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  forwardRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SELECT_OVERLAY_PANEL_CLASS } from '../../utils/dom-events/dropdown-overlay.util';
import { DropdownOverlayBase } from '../../utils/dropdown-overlay-base/dropdown-overlay-base';
import { ISelectOption } from './models/select-option.interface';
import { ISelectSettings } from './models/select-settings.interface';

@Component({
  selector: 'sgt-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    },
  ]
})
export class SelectComponent
  extends DropdownOverlayBase<number>
  implements OnChanges {
  @Input() options: ISelectOption[] = [];
  @Input() settings: Partial<ISelectSettings> = {};

  @ContentChild('customOptionTemplate') customOptionTemplate: TemplateRef<{
    $implicit: ISelectOption;
  }>;

  @Output() selectedValueChanged = new EventEmitter<ISelectOption | null>();
  @Output() isFirstOpened = new EventEmitter<boolean>();

  @ViewChild('selectRef') private selectRef: ElementRef<HTMLElement>;
  @ViewChild('searchInputRef') private searchInputRef?: ElementRef<HTMLInputElement>;

  private _isFirstOpen = true;
  protected readonly overlayPanelClass = SELECT_OVERLAY_PANEL_CLASS;

  protected get triggerElement(): HTMLElement {
    return this.selectRef.nativeElement;
  }

  selectedOption: ISelectOption | null = null;
  searchText = '';

  get filteredOptions(): ISelectOption[] {
    const texto = this.searchText.trim().toLowerCase();

    if (!texto) {
      return this.options;
    }

    return this.options.filter((opt) => opt.label?.toLowerCase().includes(texto));
  }

  private opcaoPadrao: ISelectOption | null = null;

  private defaultSettings: ISelectSettings = {
    placeholder: 'Selecione uma opção',
    emptyResultMessage: 'Nenhuma opção encontrada',
    isClearSelectionAllowed: false,
    hasSearchField: false,
    searchEmptyResultMessage: 'Nenhuma opção encontrada'
  };

  constructor(
    elementRef: ElementRef<HTMLElement>,
    ngZone: NgZone,
    changeDetectorRef: ChangeDetectorRef,
    overlay: Overlay
  ) {
    super(elementRef, ngZone, changeDetectorRef, overlay);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.opcaoPadrao = this.options?.find((opt) => opt.isSelected) ?? null;
    }
    this.settings = { ...this.defaultSettings, ...this.settings };
    this.setDisabledPorSettings(!!this.settings.isDisabled);
    this.updateValue(this.value);

    if (this.isOpen && !this.settings.isLoading) {
      this.focarBusca();
    }
  }

  private fecharDropdown(): void {
    this.isOpen = false;
    this.encerrarObservacaoDeScroll();
  }

  protected aoFecharPorInteracaoExterna(): void {
    this.fecharDropdown();
    this.markAsTouched();
  }

  open(): void {
    const estavaAberto = this.isOpen;
    this.alternarAberturaDoDropdown();

    if (!this.isOpen || estavaAberto) {
      return;
    }

    this.searchText = '';
    this.focarBusca();

    if (this._isFirstOpen) {
      this._isFirstOpen = false;
      this.isFirstOpened.emit(true);
    }
  }

  clearSearch(): void {
    this.searchText = '';
    this.focarBusca();
  }

  private focarBusca(): void {
    if (this.settings.hasSearchField) {
      setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
    }
  }

  selectOption(option: ISelectOption): void {
    this.setSelectedOption(option);
    this.fecharDropdown();
    this.emitChange(option.id);
    this.selectedValueChanged.emit(option);
    this.markAsTouched();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    this.setSelectedOption(null);
    this.selectedValueChanged.emit(null);
    this.emitChange(null);
    this.markAsTouched();
  }

  protected updateValue(value: number | null): void {
    if (!this.options?.length) {
      this.selectedOption = null;
      return;
    }

    const option = value != null ? this.options.find((opt) => opt.id === value) : this.opcaoPadrao;

    this.setSelectedOption(option ?? null);
  }

  private setSelectedOption(option: ISelectOption | null): void {
    this.options.forEach((opt) => (opt.isSelected = false));
    this.selectedOption = option;

    if (option) {
      option.isSelected = true;
    }
  }

}
